<template>
  <div class="excel-grid-wrap xls-scroll" ref="gridWrap">
    <table class="excel-grid">
      <thead>
        <tr>
          <th
            v-for="c in columns"
            :key="c.key"
            :class="{ 'excel-th-active': isColumnFiltered(c.key) || sortState.key === c.key }"
          >
            <div class="excel-th-inner">
              <span class="excel-th-label">{{ c.label }}</span>
              <button
                v-if="isFilterable(c)"
                type="button"
                class="excel-filter-btn"
                :class="{ active: isColumnFiltered(c.key) || sortState.key === c.key }"
                @click.stop="toggleFilterMenu(c.key, $event)"
                :title="'Filter / sort ' + c.label"
              >
                <i class="fa fa-filter"></i>
              </button>
            </div>

            <teleport to="body" v-if="isFilterable(c)">
              <div
                v-if="openFilterKey === c.key"
                class="excel-filter-menu"
                :style="{ top: menuPos.top + 'px', left: menuPos.left + 'px' }"
                @click.stop
              >
                <div class="excel-filter-sort">
                  <button
                    type="button"
                    class="excel-filter-sort-btn"
                    :class="{ active: sortState.key === c.key && sortState.dir === 'asc' }"
                    @click="applySort(c.key, 'asc')"
                  >
                    <i class="fa fa-sort-amount-up-alt"></i> Sort Ascending
                  </button>
                  <button
                    type="button"
                    class="excel-filter-sort-btn"
                    :class="{ active: sortState.key === c.key && sortState.dir === 'desc' }"
                    @click="applySort(c.key, 'desc')"
                  >
                    <i class="fa fa-sort-amount-down-alt"></i> Sort Descending
                  </button>
                </div>

                <div class="excel-filter-search">
                  <input
                    type="text"
                    v-model="filterSearchText"
                    placeholder="Search values"
                  />
                </div>

                <div class="excel-filter-values xls-scroll">
                  <label class="excel-filter-value-item excel-filter-selectall">
                    <input
                      type="checkbox"
                      :checked="isAllSelected(c.key)"
                      @change="toggleSelectAll(c.key)"
                    />
                    (Select All)
                  </label>
                  <label
                    v-for="val in filteredUniqueValues(c.key)"
                    :key="val"
                    class="excel-filter-value-item"
                  >
                    <input
                      type="checkbox"
                      :checked="isValueSelected(c.key, val)"
                      @change="toggleValue(c.key, val)"
                    />
                    {{ val === "" ? "(Blanks)" : val }}
                  </label>
                  <div v-if="!filteredUniqueValues(c.key).length" class="excel-filter-novalues">
                    No matching values
                  </div>
                </div>

                <div class="excel-filter-actions">
                  <button type="button" class="excel-filter-clear" @click="clearColumnFilter(c.key)">
                    Clear
                  </button>
                  <button type="button" class="excel-filter-ok" @click="openFilterKey = null">
                    OK
                  </button>
                </div>
              </div>
            </teleport>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="!displayRows.length">
          <td :colspan="columns.length" class="excel-empty">
            {{ noDataText }}
          </td>
        </tr>
        <tr v-for="(row, idx) in displayRows" :key="idx">
          <td
            v-for="c in columns"
            :key="c.key"
            :class="[
              'excel-cell',
              c.align ? 'text-' + c.align : '',
              c.fill && row[c.key + '_color'] ? ['excel-cell-fill', row[c.key + '_color']] : '',
            ]"
          >
            <span v-if="c.fill" :class="{ 'excel-plain': !row[c.key + '_color'] }">{{
              row[c.key] || "-"
            }}</span>
            <span
              v-else-if="c.block && row[c.key + '_color']"
              class="excel-pill"
              :class="row[c.key + '_color']"
            >
              <span
                v-if="row[c.key + '_icon'] === 'bandage'"
                class="excel-pill-bandage"
                aria-hidden="true"
              ></span>
              <span
                v-else-if="row[c.key + '_icon'] === 'cross'"
                class="excel-pill-cross"
                aria-hidden="true"
                >&times;</span
              >
              <span>{{ row[c.key] || "-" }}</span>
            </span>
            <span v-else-if="c.block" class="excel-plain">{{ row[c.key] || "-" }}</span>
            <span v-else-if="c.money">{{ currencySymbol }} {{ formatMoney(row[c.key]) }}</span>
            <span v-else-if="c.date">{{ formatDate(row[c.key]) }}</span>
            <template v-else-if="c.link">
              <a
                v-if="row[c.key]"
                href="#"
                class="excel-link"
                data-bs-toggle="modal"
                :data-bs-target="modalTarget"
                @click.prevent="$emit('cell-click', { key: c.key, row })"
                >{{ row[c.key] }}</a
              >
              <span v-else>-</span>
            </template>
            <span v-else>{{ row[c.key] }}</span>
          </td>
        </tr>
      </tbody>
      <tfoot v-if="displayRows.length">
        <tr class="excel-totals">
          <td
            v-for="c in columns"
            :key="c.key"
            :class="[
              'text-' + (c.total || c.totalLabel ? 'right' : c.align || 'center'),
              c.total ? 'excel-total-cell' : '',
              c.total && c.totalAccent ? 'excel-total-accent' : '',
            ]"
          >
            <template v-if="c.total">{{ currencySymbol }} {{ formatMoney(displayTotals[c.key]) }}</template>
            <span v-else-if="c.totalLabel" class="excel-total-label">{{ c.totalLabel }}</span>
          </td>
        </tr>
      </tfoot>
    </table>
  </div>
</template>

<script>
import { formatDate } from "@/utils/dateFormat";

export default {
  name: "ExcelGridView",
  props: {
    columns: {
      type: Array,
      required: true,
    },
    rows: {
      type: Array,
      default: () => [],
    },
    totals: {
      type: Object,
      default: () => ({}),
    },
    currencySymbol: {
      type: String,
      default: "£",
    },
    noDataText: {
      type: String,
      default: "No records match the current filters.",
    },
    modalTarget: {
      type: String,
      default: "#expenseFile",
    },
  },
  emits: ["cell-click"],
  data() {
    return {
      openFilterKey: null,
      filterSearchText: "",
      columnFilters: {},
      sortState: { key: null, dir: null },
      menuPos: { top: 0, left: 0 },
    };
  },
  mounted() {
    document.addEventListener("click", this.handleOutsideClick);
    window.addEventListener("scroll", this.handleScroll, true);
    window.addEventListener("resize", this.handleWindowResize);
    window.addEventListener("orientationchange", this.handleWindowResize);
    this.updateHeight();
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleOutsideClick);
    window.removeEventListener("scroll", this.handleScroll, true);
    window.removeEventListener("resize", this.handleWindowResize);
    window.removeEventListener("orientationchange", this.handleWindowResize);
  },
  computed: {
    filteredRows() {
      const activeKeys = Object.keys(this.columnFilters);
      if (!activeKeys.length) return this.rows;
      return this.rows.filter((row) =>
        activeKeys.every((key) => {
          const selected = this.columnFilters[key];
          const col = this.columns.find((c) => c.key === key);
          return selected.includes(this.cellDisplayValue(row, col));
        })
      );
    },
    displayRows() {
      const { key, dir } = this.sortState;
      if (!key) return this.filteredRows;
      const mult = dir === "desc" ? -1 : 1;
      return [...this.filteredRows].sort((a, b) => {
        const av = a[key];
        const bv = b[key];
        const an = parseFloat(av);
        const bn = parseFloat(bv);
        if (av !== "" && bv !== "" && !isNaN(an) && !isNaN(bn)) {
          return (an - bn) * mult;
        }
        return String(av ?? "").localeCompare(String(bv ?? "")) * mult;
      });
    },
    isFiltered() {
      return Object.keys(this.columnFilters).length > 0;
    },
    displayTotals() {
      if (!this.isFiltered) return this.totals;
      const t = {};
      this.columns.forEach((c) => {
        if (c.total) {
          t[c.key] = this.displayRows.reduce(
            (sum, r) => sum + (parseFloat(r[c.key]) || 0),
            0
          );
        }
      });
      return t;
    },
  },
  methods: {
    formatDate,
    formatMoney(v) {
      return Number(v || 0).toLocaleString("en-GB", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    isFilterable(c) {
      return c.key !== "no" && !c.link;
    },
    cellDisplayValue(row, col) {
      if (!col) return "";
      const v = row[col.key];
      if (col.money) return this.formatMoney(v);
      if (col.date) return formatDate(v);
      if (v === null || v === undefined) return "";
      return String(v);
    },
    uniqueValuesForColumn(key) {
      const col = this.columns.find((c) => c.key === key);
      if (col && col.date) {
        // Order on the raw yyyy-mm-dd value so the list stays chronological,
        // then format for display.
        const raw = Array.from(new Set(this.rows.map((row) => row[key] ?? "")));
        raw.sort((a, b) => String(a).localeCompare(String(b)));
        return raw.map((v) => formatDate(v));
      }
      const values = new Set(this.rows.map((row) => this.cellDisplayValue(row, col)));
      return Array.from(values).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    },
    filteredUniqueValues(key) {
      const all = this.uniqueValuesForColumn(key);
      if (!this.filterSearchText) return all;
      const q = this.filterSearchText.toLowerCase();
      return all.filter((v) => v.toLowerCase().includes(q));
    },
    isColumnFiltered(key) {
      return Object.prototype.hasOwnProperty.call(this.columnFilters, key);
    },
    isAllSelected(key) {
      if (!this.isColumnFiltered(key)) return true;
      const all = this.uniqueValuesForColumn(key);
      return all.every((v) => this.columnFilters[key].includes(v));
    },
    isValueSelected(key, val) {
      if (!this.isColumnFiltered(key)) return true;
      return this.columnFilters[key].includes(val);
    },
    toggleSelectAll(key) {
      if (this.isAllSelected(key)) {
        delete this.columnFilters[key];
      } else {
        this.columnFilters[key] = this.uniqueValuesForColumn(key);
      }
    },
    toggleValue(key, val) {
      const all = this.uniqueValuesForColumn(key);
      const current = this.isColumnFiltered(key)
        ? [...this.columnFilters[key]]
        : [...all];
      const pos = current.indexOf(val);
      if (pos > -1) {
        current.splice(pos, 1);
      } else {
        current.push(val);
      }
      if (current.length === all.length) {
        delete this.columnFilters[key];
      } else {
        this.columnFilters[key] = current;
      }
    },
    clearColumnFilter(key) {
      delete this.columnFilters[key];
      this.filterSearchText = "";
      this.openFilterKey = null;
    },
    applySort(key, dir) {
      if (this.sortState.key === key && this.sortState.dir === dir) {
        this.sortState = { key: null, dir: null };
      } else {
        this.sortState = { key, dir };
      }
      this.openFilterKey = null;
    },
    toggleFilterMenu(key, evt) {
      if (this.openFilterKey === key) {
        this.openFilterKey = null;
        return;
      }
      const th = evt.currentTarget.closest("th");
      const rect = th.getBoundingClientRect();
      // Keep the menu inside the viewport on both axes - on a phone the
      // header cell can sit further right than the menu is wide.
      const menuWidth = Math.min(220, window.innerWidth - 16);
      this.menuPos = {
        top: Math.max(8, Math.min(rect.bottom, window.innerHeight - 40)),
        left: Math.max(8, Math.min(rect.left, window.innerWidth - menuWidth - 8)),
      };
      this.filterSearchText = "";
      this.openFilterKey = key;
    },
    handleOutsideClick(e) {
      if (this.openFilterKey && !e.target.closest(".excel-filter-menu, .excel-filter-btn")) {
        this.openFilterKey = null;
      }
    },
    handleScroll(e) {
      if (this.openFilterKey && !e.target.closest?.(".excel-filter-menu")) {
        this.openFilterKey = null;
      }
    },
    handleWindowResize() {
      if (this.openFilterKey) this.openFilterKey = null;
      this.updateHeight();
    },
    updateHeight() {
      const el = this.$refs.gridWrap;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      const available = window.innerHeight - top - 50;
      el.style.maxHeight = Math.max(available, 150) + "px";
    },
  },
};
</script>

<style scoped>
.excel-grid-wrap {
  background: #fff;
  /* The grid keeps its own horizontal scroller - never let a parent page
     style (e.g. a page-level .custom-scroll) clip it, or the right-hand
     columns become unreachable on narrow screens. */
  overflow-x: auto !important;
  overflow-y: auto;
  width: 100%;
  max-width: 100%;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-x: contain;
  border: 1px solid #d0d0d0;
  border-radius: 4px;
}
/* Self-contained scrollbar styling so the component does not rely on the
   host page's .custom-scroll class. */
.xls-scroll::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}
.xls-scroll::-webkit-scrollbar-track {
  background: #f1f1f1;
}
.xls-scroll::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}
.xls-scroll::-webkit-scrollbar-thumb:hover {
  background: #555;
}
.excel-grid {
  border-collapse: collapse;
  width: 100%;
  font-family: Arial, Helvetica, sans-serif;
  font-size: 12.5px;
  min-width: 900px;
}
.excel-grid th,
.excel-grid td {
  border: 1px solid #d0d0d0;
  height: 32px;
  padding: 0 8px;
  vertical-align: middle;
}
.excel-grid thead th {
  background: #f4f4f4;
  font-weight: 700;
  color: #262626;
  text-align: center;
  white-space: nowrap;
  position: relative;
}
.excel-grid thead th.excel-th-active {
  background: #dfe9d6;
}
.excel-th-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
}
.excel-filter-btn {
  border: none;
  background: transparent;
  color: #7a7a7a;
  padding: 2px 4px;
  cursor: pointer;
  line-height: 1;
  border-radius: 3px;
}
.excel-filter-btn:hover {
  background: #e2e2e2;
}
.excel-filter-btn.active {
  color: #2e7d32;
}
.excel-filter-menu {
  position: fixed;
  z-index: 2000;
  background: #fff;
  border: 1px solid #c9c9c9;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border-radius: 4px;
  width: 220px;
  max-width: calc(100vw - 16px);
  padding: 8px;
  text-align: left;
  font-weight: 400;
  white-space: normal;
  text-transform: none;
}
.excel-filter-sort {
  display: flex;
  flex-direction: column;
  gap: 2px;
  border-bottom: 1px solid #eaeaea;
  padding-bottom: 6px;
  margin-bottom: 6px;
}
.excel-filter-sort-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  border: none;
  background: transparent;
  text-align: left;
  padding: 4px 6px;
  font-size: 12px;
  color: #333;
  cursor: pointer;
  border-radius: 3px;
}
.excel-filter-sort-btn:hover {
  background: #f2f2f2;
}
.excel-filter-sort-btn.active {
  background: #e3f2e5;
  color: #2e7d32;
  font-weight: 600;
}
.excel-filter-search input {
  width: 100%;
  box-sizing: border-box;
  font-size: 12px;
  padding: 4px 6px;
  border: 1px solid #d0d0d0;
  border-radius: 3px;
  margin-bottom: 6px;
}
.excel-filter-values {
  max-height: 160px;
  overflow-y: auto;
  border: 1px solid #eee;
  border-radius: 3px;
  padding: 4px 2px;
}
.excel-filter-value-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 400;
  padding: 3px 4px;
  cursor: pointer;
  border-radius: 3px;
  text-transform: none;
}
.excel-filter-value-item:hover {
  background: #f2f2f2;
}
.excel-filter-selectall {
  border-bottom: 1px solid #eee;
  margin-bottom: 2px;
  padding-bottom: 5px;
}
.excel-filter-novalues {
  font-size: 12px;
  color: #999;
  padding: 6px 4px;
}
.excel-filter-actions {
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
}
.excel-filter-clear {
  border: none;
  background: transparent;
  color: #b00020;
  font-size: 12px;
  cursor: pointer;
  padding: 4px 6px;
}
.excel-filter-ok {
  border: none;
  background: #2fbf5c;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 3px;
  cursor: pointer;
}
.excel-cell {
  text-align: center;
  /* Money and dates must never wrap - with the wider invoice grid there are
     enough columns to squeeze them. Only the left-aligned text may wrap. */
  white-space: nowrap;
}
.excel-cell.text-left {
  text-align: left;
  white-space: normal;
}
.excel-cell.text-right {
  text-align: right;
}
.excel-empty {
  text-align: center;
  color: #888;
  padding: 20px;
}
/* Status / category columns paint the whole cell rather than a pill, so the
   grid reads like a conditionally formatted spreadsheet. */
.excel-cell-fill {
  font-weight: 600;
  text-transform: capitalize;
  color: #fff;
}
.excel-cell-fill.xls-badge-green {
  background: #4cb050;
}
.excel-cell-fill.xls-badge-orange {
  background: #f9a04c;
  color: #3a3a3a;
}
.excel-cell-fill.xls-badge-blue {
  background: #8aa4f0;
}
.excel-cell-fill.xls-badge-brown {
  background: #8b5a2b;
}
.excel-cell-fill.xls-badge-red {
  background: #e01010;
}
.excel-cell-fill.xls-badge-purple {
  background: #c9aad4;
  color: #3a3a3a;
}
.excel-cell-fill.xls-badge-grey {
  background: #ececed;
  color: #6a6a70;
}
.excel-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 11.5px;
  font-weight: 600;
  color: #fff;
  min-width: 64px;
  text-transform: capitalize;
  cursor: default;
}
.excel-pill.xls-badge-green {
  background: #4cb050;
}
.excel-pill.xls-badge-orange {
  background: #f9a04c;
  color: #3a3a3a;
}
.excel-pill.xls-badge-blue {
  background: #8aa4f0;
}
.excel-pill.xls-badge-brown {
  background: #8b5a2b;
}
.excel-pill.xls-badge-red {
  background: #e01010;
}
.excel-pill.xls-badge-purple {
  background: #c9aad4;
  color: #3a3a3a;
}
.excel-pill.xls-badge-grey {
  background: #ececed;
  color: #6a6a70;
}
/* Attachment Yes / No pills */
.excel-pill.xls-pill-yes,
.excel-pill.xls-pill-no {
  border-radius: 18px;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 500;
  min-width: 70px;
  white-space: nowrap;
}
.excel-pill.xls-pill-yes {
  background: #f1f8f3;
  color: #2e6544;
}
.excel-pill.xls-pill-no {
  background: #f6f6f7;
  color: #6d7892;
}
.excel-pill-bandage {
  position: relative;
  width: 14px;
  height: 9px;
  border-radius: 4px;
  background: #76a888;
  transform: rotate(-48deg);
  box-shadow: inset 0 0 0 1px rgba(35, 103, 62, 0.08);
  flex: none;
}
.excel-pill-bandage::before {
  content: "";
  position: absolute;
  top: 2px;
  left: 3.5px;
  width: 2px;
  height: 2px;
  border-radius: 50%;
  background: #568b69;
  box-shadow: 3.5px 0 #568b69, 0 3.5px #568b69, 3.5px 3.5px #568b69;
}
.excel-pill-cross {
  color: #b8babe;
  font-size: 15px;
  font-weight: 500;
  line-height: 1;
  margin-top: -1px;
  flex: none;
}
.excel-link {
  color: #1155cc;
  text-decoration: underline;
  cursor: pointer;
}
.excel-totals td {
  font-weight: 700;
  background: #eef3ee;
  text-align: center;
}
.excel-totals td.text-left {
  text-align: left;
}
.excel-totals td.text-right {
  text-align: right;
}
.excel-total-cell {
  background: #b7d7a8 !important;
}
/* The balance-due total is the number that needs chasing - call it out. */
.excel-total-accent {
  background: #f4b183 !important;
}
.excel-plain {
  color: #6a6a70;
}
.excel-total-label {
  font-weight: 700;
  color: #262626;
}

/* Tablet / phone: tighten the cells so more of the grid fits before the
   horizontal scroller kicks in, and keep the filter menu inside the viewport. */
@media (max-width: 991.98px) {
  .excel-grid {
    font-size: 12px;
    min-width: 820px;
  }
  .excel-grid th,
  .excel-grid td {
    padding: 0 6px;
  }
}
@media (max-width: 575.98px) {
  .excel-grid {
    font-size: 11.5px;
    min-width: 720px;
  }
  .excel-grid th,
  .excel-grid td {
    height: 30px;
    padding: 0 5px;
  }
  .excel-pill {
    min-width: 0;
    padding: 3px 8px;
    font-size: 11px;
  }
  .excel-pill.xls-pill-yes,
  .excel-pill.xls-pill-no {
    min-width: 0;
    padding: 3px 10px;
    font-size: 11px;
  }
  .excel-filter-values {
    max-height: 130px;
  }
}
</style>
